import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoPopper from "../popper/InfoPopper";
import {
  DateView,
  LocalizationProvider,
  MobileDatePicker,
} from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CSSProperties, useEffect, useState } from "react";
import "dayjs/locale/th";
import { CustomFormHelperText } from "./CustomFormHelperText";
import { common } from "../../i18n";
import { Calendar } from "../icon/Calendar";

export const iconStyle: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "12px",
  transform: "translate(0, -50%)",
  pointerEvents: "none",
};

export const inputProps = {
  style: {
    padding: "0px 0px !important",
    width: "100%",
    marginLeft: "32px",
    // backgroundColor: "var(--white)",
  },
};

interface Props {
  label: string;
  info: string;
  disabled?: boolean;
  required?: boolean;
  defaultDate: Dayjs | null;
  value: Dayjs | null;
  inputRef?: any;
  error?: FieldError;
  initDate?: Dayjs | null;
  handleOnChange?: (...event: any[]) => void;
  format?: string;
  views?: DateView[];
  showInfo?: boolean;
  disableFuture?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  clearable?: boolean;
}

const CustomDatePicker = (props: Props) => {
  const { t, i18n } = useTranslation([common], { nsMode: "fallback" });

  const [tempValue, setTempValue] = useState<Dayjs | null>(props.value ?? null);

  useEffect(() => {
    if (props.value !== tempValue) setTempValue(props.value);
  }, [props.value]);

  return (
    <LocalizationProvider
      adapterLocale={i18n.language.split("-")[0] ?? i18n.language}
      dateAdapter={AdapterDayjs}
    >
      {props.showInfo === undefined ||
      (props.showInfo && props.showInfo === true) ? (
        <Typography
          variant="h3"
          color={"var(--dark)"}
          sx={{ marginBottom: "8px" }}
        >
          {props.label}
          {props.required === true ? (
            <span style={{ color: "var(--red)" }}> * </span>
          ) : (
            <span> ({t("Optional")}) </span>
          )}
          {props.label !== props.info && <InfoPopper text={props.info} />}
        </Typography>
      ) : null}

      <Box width={"100%"} display={"block"} position={"relative"}>
        <MobileDatePicker
          value={tempValue}
          defaultValue={props.defaultDate}
          onChange={(newValue) => setTempValue(newValue)} // แค่เก็บชั่วคราว ยังไม่อัปเดตจริง
          onAccept={(newValue) => {
            setTempValue(newValue); // อัปเดต UI ด้วย
            props.handleOnChange?.(newValue); // ส่งค่าออกจริงเมื่อกดตกลง
          }}
          format={props.format ?? "MM/YYYY"}
          minDate={props.minDate ?? dayjs(`2000-01-01`)}
          maxDate={props.maxDate ?? dayjs(`${dayjs().year()}-12`)}
          views={props.views ?? ["year", "month"]}
          inputRef={props.inputRef}
          monthsPerRow={3}
          yearsPerRow={3}
          slotProps={{
            textField: {
              error: !!props.error,
              size: "small",
              inputProps: inputProps,
            },
            actionBar: { actions: [] }, // ซ่อนปุ่มทั้งหมด
          }}
          sx={{ width: "100%", height: "40px" }}
          localeText={{
            toolbarTitle: props.label,
            okButtonLabel: t("Ok"),
            cancelButtonLabel: t("Cancel"),
          }}
          disableFuture={props.disableFuture}
          disabled={props.disabled}
          label={props.showInfo === false ? props.label : undefined}
          closeOnSelect
        />

        <Calendar style={iconStyle} />

        {props.clearable && tempValue && !props.disabled && (
          <IconButton
            size="small"
            aria-label="clear"
            onClick={(e) => {
              e.stopPropagation();
              setTempValue(null);
              props.handleOnChange?.(null);
            }}
            sx={{
              position: "absolute",
              top: "50%",
              right: "8px",
              transform: "translate(0, -50%)",
              padding: "4px",
            }}
          >
            <CloseIcon sx={{ fontSize: "16px", color: "var(--gray4)" }} />
          </IconButton>
        )}
      </Box>
      <CustomFormHelperText error={props.error} />
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
